/**
 * The one definition, in the admin UI, of "is nothing payable on this booking
 * because an organization covers it?" (#1926).
 *
 * A covered booking is `payment_status: 'unpaid'` on purpose — no money moved,
 * so no money event was fabricated — and every surface that renders the payment
 * question has to know that, or the same booking answers differently depending
 * on which screen you open. That is the failure this codebase already paid for
 * once, when "reserved" was decided in five places (#1875 → #1890).
 *
 * Every surface that asks it imports this module: the payment cell and the
 * series payment summary in `BookingTable`, the CSV export (status and open
 * amount), the detail pages' badge and invoice block, and the price summaries
 * (`coveredByOf` below). The payment badge also carries "free by price"
 * (`paymentBadgeStatus` below), the other reason a booking owes nothing. The server-side
 * definition lives in `shared/src/memberRoomAccess.ts`; this one
 * only reads the flag the server already computed.
 */
export const isCoveredByOrganization = (booking: {
    coveredByOrg?: boolean;
}): boolean => booking.coveredByOrg === true;

/** Status key used wherever payment states are counted or coloured. */
export const COVERED_PAYMENT_STATUS = 'covered' as const;

/**
 * Is this booking free BY PRICE (PO, 2026-10-23)? Its list price before any
 * discount is 0 € — a 0 € row, a class at 0 €, "Entgelt kostenfrei". The
 * server decides it (`isFreeByPrice`, shared/src/freeByPrice.ts, sent as
 * `freeByPrice` on the booking details); this only reads the flag. A booking a
 * voucher or a manual price brought down to 0 € is not free by price: it keeps
 * its payment status and its invoice.
 */
export const isFreeByPriceBooking = (booking: { freeByPrice?: boolean } | undefined): boolean =>
    booking?.freeByPrice === true;

/** Status key of the "Kostenfrei" payment badge. Not a payment state: nothing was owed. */
export const FREE_PAYMENT_STATUS = 'free' as const;

/** A payment badge: the booking's own status, or one of the two "nothing owed" readings. */
export type PaymentBadge<S extends string> = S | typeof COVERED_PAYMENT_STATUS | typeof FREE_PAYMENT_STATUS;

/**
 * The payment states "Kostenfrei" replaces. Nothing moved on a free booking, so
 * `unpaid` (a request or a staff booking) and `paid` (the free completion and
 * fully discounted creation write it) both say something untrue about money.
 * A state that records money that DID move — partial, over-, refunded,
 * disputed — is kept: that is a fact worth seeing.
 */
const REPLACED_BY_FREE = new Set(['unpaid', 'paid']);

/** "covered" or "free" when nothing is owed on the booking, else null. */
const nothingOwedReading = (
    details: { coveredByOrg?: boolean, freeByPrice?: boolean } | undefined,
    paymentStatus: string,
): typeof COVERED_PAYMENT_STATUS | typeof FREE_PAYMENT_STATUS | null => {
    if (details?.coveredByOrg === true) return COVERED_PAYMENT_STATUS;
    const replaced = REPLACED_BY_FREE.has(String(paymentStatus ?? '').toLowerCase());
    return isFreeByPriceBooking(details) && replaced ? FREE_PAYMENT_STATUS : null;
};

/**
 * The payment badge for a booking, given its payment status: "covered" for a
 * covered booking, "free" for a booking free by price, its payment status
 * otherwise. ONE rule for every surface that renders the payment question —
 * the detail and edit pages, the series page and the CSV export. Display only:
 * the stored `payment_status` is never rewritten, because `paid` would flip
 * `isBookingReserved` and a free request waiting for the operator would read as
 * booked.
 */
export const paymentBadgeStatus = <S extends string>(
    details: { coveredByOrg?: boolean, freeByPrice?: boolean } | undefined,
    paymentStatus: S,
): PaymentBadge<S> => nothingOwedReading(details, paymentStatus) ?? paymentStatus;

/**
 * The payment badge of a booking's DETAIL page. The detail response spells the
 * payment status `payment_status`.
 */
export const detailPaymentBadgeStatus = <S extends string>(
    details: { coveredByOrg?: boolean, freeByPrice?: boolean, payment_status: S },
): PaymentBadge<S> =>
        paymentBadgeStatus(details, details.payment_status);

/**
 * The payment badge of a booking's EDIT page: the edit form keeps its payment
 * status in its own state, coverage and price come from the detail response.
 * Same answer as `detailPaymentBadgeStatus`.
 */
export const editPaymentBadgeStatus = <S extends string>(
    details: { coveredByOrg?: boolean, freeByPrice?: boolean } | undefined,
    paymentStatus: S,
): PaymentBadge<S> =>
        paymentBadgeStatus(details, paymentStatus);

/** The covering organization's name on a covered booking, null otherwise. */
export const coveringOrganizationNameOf = (
    details: { coveredByOrg?: boolean, coveringOrganizationName?: string | null } | undefined,
): string | null =>
    details?.coveredByOrg === true ? details.coveringOrganizationName ?? null : null;

/**
 * What the price summary needs to know about a covered booking: that it is
 * covered, and by whom. Null when the booking is not covered — the summary
 * then shows its "Fälliger Betrag" as always.
 */
export interface CoveredBy {
    organizationName: string | null;
}

export const coveredByOf = (
    details: { coveredByOrg?: boolean, coveringOrganizationName?: string | null } | undefined,
): CoveredBy | null =>
    (details && isCoveredByOrganization(details) ? { organizationName: coveringOrganizationNameOf(details) } : null);

/**
 * A series is covered when every date is (it is booked for one organization,
 * so in practice all or none are): then no amount is due from the customer. A
 * mixed series keeps its summed balance — its uncovered dates are due.
 *
 * A COPY of `isSeriesCovered` in `shared/src/coveredAmountDue.ts`, which the
 * series mails use (`new-backend/admin/src/lib/series/seriesCoverage.ts`); the
 * admin UI does not build from `@kolula/shared`. Both are pinned to
 * `shared/booking/series-coverage-vectors.json` (`bookingCoverage.test.ts`
 * here, `backend/tests/coveredAmountDue.test.ts` there) — change them together.
 */
export const seriesCoveredBy = (
    details: Array<{ coveredByOrg?: boolean, coveringOrganizationName?: string | null }>,
): CoveredBy | null =>
    (details.length > 0 && details.every((d) => coveredByOf(d)) ? coveredByOf(details[0]) : null);

export default isCoveredByOrganization;
